import React, { useRef, useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useUser } from "../../hook/useUser.js";
import { getElementsDataApi, getMainModelDataApi, getBackgroundModelUrlApi } from "../../api/model";
import { getElementStatus } from "../../utils/schedule.js";
import { formatNumberWithCommas } from "../../utils/numberFormatter.js";
import { getQTOIDApi } from "../../api/qto";
import { getContractorsApi } from "../../api/member.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import IFCViewer from "../../component/bim/IFCViewer.js";
import SelectComponent from "../../component/SelectComponent.js";
import MainTable from "../../component/table/MainTable.js";
import ManageQTOPopup from "./components/ManageQTOPopup.js";
import QTOElementsPopup from "./components/QTOElementsPopup.js";

const QuantityPage = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { showError } = useContext(NotificationContext);
  const { user } = useUser(projectId);
  const container = useRef();
  const [elementIds, setElementIds] = useState({
    planProgress: [],
    actualProgress: [],
    delayed: [],
    planComplete: [],
    actualComplete: [],
    actualHide: [],
    hideElementIds: [],
    showFilter: [],
    white: [],
    all: [],
  });
  const [state, setState] = useState({
    contractorOptions: [],
    contractorElementValue: [],
    workSetOptions: [],
    viewOptions: [
      { value: "Full view", label: "Full view" },
      { value: "Actual view", label: "Actual view" },
      { value: "Plan view", label: "Plan view" },
    ],
    backgroundOptions: [],
    worksetValue: [],
    viewValue: "",
    backgroundValue: [],
    backgroundModels: [],
    currentDate: dayjs(),
    showPopup: false,
    modelURL: [],
    elementsData: [],
  });
  const qtoTableHeaders = [
    { id: "qtoId", label: "QTO ID", align: "left", defaultSortOrder: "asc" },
    { id: "qtoItemName", label: "QTO Item Name", align: "left" },
    { id: "unitRate", label: "Unit Rate(HKD)", align: "left" },
    { id: "unit", label: "Unit", align: "left" },
    { id: "count", label: "Count", align: "left" },
    { id: "totalCost", label: "Total Cost (HKD)", align: "left", type: "number" },
  ];

  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [qtoTableData, setQTOTableData] = useState([]);
  const [selectedQTOId, setSelectedQTOId] = useState([]);
  const [selectedBIMId, setSelectedBIMId] = useState([]);
  const [commonProps, setCommonProps] = useState({});
  const [originalQTOTableData, setOriginalQTOTableData] = useState([]);
  const [qtoElementsPopup, setQTOElementsPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);

  const selectComponents = [
    {
      Component: SelectComponent,
      options: state.workSetOptions,
      value: state.worksetValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          worksetValue: value,
          modelURL: state.modelURL.map((mainModel) => {
            return {
              ...mainModel,
              visible: value.includes(mainModel.workset),
            };
          }),
        }));
      },
      selectLabel: "Workset",
      placeholder: "select an item...",
    },
    {
      Component: SelectComponent,
      options: state.viewOptions,
      value: state.viewValue,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          viewValue: value,
        }));
      },
      selectLabel: "View",
    },
    {
      Component: SelectComponent,
      options: state.contractorOptions,
      value: state.contractorElementValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          contractorElementValue: value,
        }));
      },
      selectLabel: "Contractor",
      placeholder: "select an item...",
    },
    {
      Component: SelectComponent,
      options: state.backgroundOptions,
      value: state.backgroundValue,
      isMultiSelect: true,
      required: false,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          backgroundValue: value,
          backgroundModels: state.backgroundModels.map((backgroundModel) => {
            return {
              ...backgroundModel,
              visible: value.includes(backgroundModel.fileURL),
            };
          }),
        }));
      },
      selectLabel: "Background",
      placeholder: "Select an item...",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([getContractorsData(), getMainModel(), getBackgroundModelUrl(), getQTOData()]);
      setState((prevState) => ({
        ...prevState,
        viewValue: "Actual view",
      }));
      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (state.viewValue === "Actual view") {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: [...elementIds.actualHide, ...elementIds.hideElementIds],
        delayedNotStartElementIds: elementIds.delayedNotStart,
        inProgressElementIds: elementIds.actualProgress,
        delayedElementIds: elementIds.delayed,
      }));
    } else if (state.viewValue === "Plan view") {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: [...elementIds.planHide, ...elementIds.hideElementIds],
        inProgressElementIds: elementIds.planProgress,
      }));
    } else {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: elementIds.hideElementIds,
        delayedNotStartElementIds: [],
        inProgressElementIds: [],
        delayedElementIds: [],
      }));
    }
  }, [state.viewValue, elementIds]);

  useEffect(() => {
    if (state.worksetValue.length > 0 && state.elementsData.length > 0) {
      colorRulesData(state.elementsData);
    }
  }, [state.worksetValue, state.elementsData, state.currentDate, state.contractorElementValue]);

  const searchButton = async (searchString) => {
    if (!searchString.trim()) {
      setQTOTableData(originalQTOTableData);
    } else {
      const filteredData = originalQTOTableData.filter((item) => item.qtoItemName.toLowerCase().includes(searchString.toLowerCase().trim()));
      setQTOTableData(filteredData);
    }
  };

  const resetButton = async () => {
    setSearch("");
    searchButton("");
  };

  const colorRulesData = (data) => {
    const elementStatus = getElementStatus(data, state.currentDate);
    let allIds = data?.map((item) => item?.bimElementId);
    setElementIds((prevState) => ({
      ...prevState,
      ...elementStatus,
      all: allIds,
      // filter contractor
      hideElementIds:
        state.contractorElementValue.length === 0 || state.contractorElementValue.length === state.contractorOptions.length
          ? []
          : data.filter((item) => !state.contractorElementValue.includes(item.contractor)).map((item) => item?.bimElementId),
    }));
  };

  //get main model url and workset
  const getMainModel = async () => {
    const result = await getMainModelDataApi(projectId);
    const data = result?.data;

    if (data && Array.isArray(data)) {
      const mainModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });

      setState((prevState) => ({
        ...prevState,
        modelURL: mainModels,
      }));

      const uniqueWorksets = new Set(data.map((item) => item.workset));
      const workSetOptionsArray = Array.from(uniqueWorksets).map((workset) => ({
        value: workset,
        label: workset,
      }));
      setState((prevState) => ({
        ...prevState,
        workSetOptions: workSetOptionsArray,
      }));

      if (uniqueWorksets.size > 0) {
        setState((prevState) => ({
          ...prevState,
          worksetValue: Array.from(uniqueWorksets),
        }));
      }
      await getElementsData();
    }
  };

  //get background model url&filter
  const getBackgroundModelUrl = async () => {
    const result = await getBackgroundModelUrlApi(projectId);
    const data = result?.data;
    if (data && Array.isArray(data)) {
      const backgroundModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: false,
        };
      });
      const backgroundArray = backgroundModels.map((item) => ({
        value: item.fileURL,
        label: item.filename,
      }));
      setState((prevState) => ({
        ...prevState,
        backgroundOptions: backgroundArray,
        backgroundModels: backgroundModels,
        // backgroundValue: backgroundArray.map((b) => b.value),
        backgroundValue: [],
      }));
    }
  };

  const getElementsData = async () => {
    try {
      const result = await getElementsDataApi(projectId);
      const data = result?.data;
      if (!data) showError("No element data returned from API.");

      setState((prevState) => {
        const newState = {
          ...prevState,
          elementsData: data,
        };
        colorRulesData(newState.elementsData);
        return newState;
      });
    } catch {
      showError("Received element data occurred error.");
    }
  };

  const getQTOData = async () => {
    try {
      const result = await getQTOIDApi(projectId);
      const data = result?.data;
      if (!data) showError("No qto data returned from API.");
      const parsedData = data.map((c) => ({
        id: c.id,
        qtoId: c.qtoId,
        qtoItemName: c.name,
        unitRate: formatNumberWithCommas(c.quantityUnitRate),
        unit: c.unit,
        count: (
          <Typography sx={{ fontSize: "11px" }}>
            {c.unit.toLowerCase() === "no's" ? "" : formatNumberWithCommas(c.baseQuantity)}
            {getUnit(c.unit)} {" (" + c.elementIds?.length + " item" + (c.elementIds?.length > 1 ? "s)" : ")") || 0}
          </Typography>
        ),
        totalCost: formatNumberWithCommas(c.cost),
        elementIds: c.elementIds,
      }));
      setQTOTableData(parsedData);
      setOriginalQTOTableData(parsedData);
    } catch {
      showError("Received qto data occurred error.");
    }
  };

  const getUnit = (unit) => {
    if (unit.toLowerCase() === "length") {
      return "m";
    }
    if (unit.toLowerCase() === "volume") {
      return (
        <>
          m<sup>3</sup>
        </>
      );
    }
    return "";
  };

  const getContractorsData = async () => {
    try {
      const result = await getContractorsApi(projectId);
      const data = result?.data;
      if (data) {
        const conTractors = new Set(data?.map((item) => item.name));
        const options = Array.from(conTractors).map((item) => ({
          label: item,
          value: item,
        }));
        setState((prevState) => ({
          ...prevState,
          contractorOptions: options,
        }));
        if (Array.from(conTractors).length > 0) {
          setState((prevState) => ({
            ...prevState,
            contractorElementValue: Array.from(conTractors),
          }));
        }
      }
    } catch {
      showError("Received contractor data occurred error.");
    }
  };

  const handleClosePopup = () => {
    setState((prevState) => ({
      ...prevState,
      showPopup: false,
    }));
  };

  //manage qto upload new version
  const handleFileUploadSuccess = () => {
    getQTOData();
  };

  const handleUpdateComplete = () => {
    getQTOData();
  };

  useEffect(() => {
    if (!selectedBIMId) {
      setSelectedQTOId([]);
      return;
    }
    const row = qtoTableData.find((item) => Array.isArray(item.elementIds) && item.elementIds.includes(selectedBIMId));

    setSelectedQTOId(row ? [row.id] : []);
  }, [selectedBIMId, qtoTableData]);

  const handleElementClick = (point, bimElementId, elementCenter) => {
    setSelectedBIMId(bimElementId);
  };

  const handleRowSelect = async (id) => {
    const selectedData = qtoTableData?.find((item) => item.id === id[0]);
    if (selectedQTOId[0] === id[0]) {
      setCommonProps((prevState) => ({
        ...prevState,
        selectionHighlightElementIds: [],
      }));
      setSelectedQTOId([]);
    } else {
      setCommonProps((prevState) => ({
        ...prevState,
        selectionHighlightElementIds: selectedData?.elementIds || [],
      }));
      setSelectedQTOId(id);
    }
    setSelectedRow(selectedData);
    setQTOElementsPopup(true);
  };

  return (
    <>
      <Box width="100%" height="100%" display="flex" gap={2}>
        <Box minWidth="600px" flex={1}>
          <IFCViewer
            ref={container}
            modelURL={state.modelURL}
            backgroundModels={state.backgroundModels}
            title={"3D Model View"}
            selectComponents={selectComponents}
            onClick={handleElementClick}
            advanced
            {...commonProps}
          ></IFCViewer>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minWidth: 800,
            flex: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Button
              onClick={() =>
                setState((prevState) => ({
                  ...prevState,
                  showPopup: true,
                }))
              }
              variant="contained"
              color="secondary"
              sx={{
                pr: 1.5,
                pl: 0,
                py: 0,
                lineHeight: "35px",
                borderRadius: "20px",
                width: 140,
              }}
              startIcon={<img src="assets/buttons/set.svg" alt="Manage QTO" />}
            >
              Manage QTO
            </Button>
            <Box display="flex" gap={1} alignItems="center">
              <Typography sx={{ minWidth: 110 }}>Search QTO Name:</Typography>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchButton(search);
                  }
                }}
                placeholder="Enter name..."
                variant="outlined"
                sx={{
                  my: 0.5,
                  width: 155,
                  "& .MuiInputBase-input": {
                    py: 0.5,
                  },
                }}
              />
              <Button
                onClick={() => searchButton(search)}
                variant="contained"
                color="secondary"
                sx={{
                  py: 0.3,
                  borderRadius: "10px",
                }}
              >
                Search
              </Button>
              <Button
                onClick={() => resetButton(search)}
                variant="outlined"
                color="inherit"
                sx={{
                  py: 0.2,
                  borderRadius: "10px",
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              height: "25px",
              lineHeight: "25px",
              fontSize: "12px",
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
              color: "#fff",
              borderRadius: "10px 10px 0 0",
            }}
          >
            <img
              src="assets/icons/qto.svg"
              alt="qto"
              style={{
                height: "20px",
                verticalAlign: "middle",
                margin: "0 8px 0 10px",
              }}
            ></img>
            Quantity Take-Off
          </Box>
          <MainTable
            paperSx={{
              borderRadius: "0 0 10px 10px!important",
              height: "calc(100% - 60px)",
            }}
            sx={{
              borderRadius: "0 0 10px 10px!important",
              height: "100%",
              maxHeight: "100%",
              ".MuiButtonBase-root.MuiTableSortLabel-root.Mui-active": {
                color: "#fff",
              },
              ".MuiButtonBase-root.MuiTableSortLabel-root:hover": {
                color: "#fff",
              },
              ".MuiTableSortLabel-icon": {
                color: "#fff!important",
                opacity: "1!important",
              },
            }}
            columns={qtoTableHeaders}
            rows={qtoTableData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                color: "#fff",
              },
            }}
            columnWidths={{
              unitRate: "150px",
            }}
            selectable="single"
            rowClickColor={"#EBFFFE"}
            selected={selectedQTOId}
            setSelected={handleRowSelect}
            sortable
            sortableColumns={["qtoId", "totalCost"]}
            isLoading={isLoading}
          />
        </Box>
      </Box>
      <ManageQTOPopup open={state.showPopup} onClose={handleClosePopup} onUploadSuccess={handleFileUploadSuccess} user={user} />
      <QTOElementsPopup
        open={qtoElementsPopup}
        onClose={() => setQTOElementsPopup(false)}
        qtoData={selectedRow}
        onUpdateComplete={handleUpdateComplete}
      />
    </>
  );
};

export default QuantityPage;
